import { observer } from 'mobx-react';
import { useId } from 'react';

import { ProductFamilyDetailsTranslation } from '../../../localization/ModuleTranslation';
import Brand from '../../../shared/Brand/Brand';
import Image from '../../../ui/Image/Image';
import LoadingStatus from '../../../ui/LoadingStatus/LoadingStatus';
import Markdown from '../../../ui/Markdown/Markdown';
import { SidePanel } from '../../../ui/SidePanel/SidePanel';
import SkeletonText from '../../../ui/SkeletonText/SkeletonText';
import css from './ProductFamilyDetails.css';
import { ProductFamilyDetailsState } from './ProductFamilyDetailsState';

interface ProductFamilyDetailsProps {
  state: ProductFamilyDetailsState;
  translation: ProductFamilyDetailsTranslation;
  onClose: () => void;
}

const ProductFamilyDetails = observer(({ onClose, state, translation }: ProductFamilyDetailsProps) => {
  const headingId = useId();

  return (
    <SidePanel className={css.root} closeLabel={translation.closeLabel} onClose={onClose} aria-labelledby={headingId}>
      <Brand className={css.brand} brandId={state.data.brandId} brandName={state.data.brandName} />
      <h2 id={headingId} className={css.heading}>
        {state.data.name}
      </h2>
      <Image
        className={css.image}
        alt=""
        src={state.data.image?.url}
        width={345}
        height={240}
        scalingMode="fit"
        sharpeningFactor={2}
        // The panel is reused as the family changes, and `Image` only settles its loading status
        // when it mounts: without a fresh one per family it keeps the previous family's status,
        // so a family with no image of its own never falls back.
        key={state.data.id}
      />
      {state.isLoading ? (
        <>
          <LoadingStatus message={translation.loadingLabel} />
          <SkeletonText className={css.descriptionSkeleton} lineCount={3} />
          <SkeletonText className={css.specificationSkeleton} lineCount={6} />
        </>
      ) : (
        <>
          {state.data.description && <p className={css.description}>{state.data.description}</p>}
          {state.data.keyFeatures && (
            <section>
              <h3 className={css.subheading}>{translation.specificationHeading}</h3>
              <Markdown className={css.description} content={state.data.keyFeatures} />
            </section>
          )}
        </>
      )}
    </SidePanel>
  );
});

export default ProductFamilyDetails;
